import moment from 'moment';
import {
  IAlternative,
  IAlternativeBenefit,
  IAlternativeContent,
  IAlternativeEmployee,
  IAlternativeEmployeesGroup,
  IBaseAlternative,
} from 'types/income';
import { converters as tableConverters } from 'store/table';
import { loaders } from './alternatives.loaders';
import {
  Alternative,
  AlternativeBenefit,
  AlternativeContent,
  AlternativeEmployee,
  AlternativeEmployeesGroup,
  BaseAlternative,
} from './alternatives.types';

const convertBaseAlternative = (
  alternative: IBaseAlternative,
): BaseAlternative => {
  return {
    id: alternative.id,
    name: alternative.name,
    isActive: alternative.isActive,
    assignments: alternative.assignments,
    benefits: alternative.benefits,
    selections: alternative.selections,
    missingSelections: alternative.missingSelections,
  };
};

const convertAlternative = (alternative: IAlternative): Alternative => {
  return {
    id: alternative.id,
    name: alternative.name,
    isActive: alternative.isActive,
    assignments: alternative.assignedEmployeesCount,
    benefits: NaN,
    selections: NaN,
    missingSelections: NaN,
    assignedGroupsCount: alternative.assignedGroupsCount,
    maxSelections: alternative.maxSelections,
    deadlineType: alternative.deadlineType,
    deadlineRange: alternative.deadlineRange,
    deadlineInDays: alternative.deadlineInDays ?? NaN,
  };
};

const convertBaseAlternatives = (
  alternatives: IBaseAlternative[],
): BaseAlternative[] => {
  return alternatives.map(convertBaseAlternative);
};

const convertEmployeesGroup = (
  employeesGroup: IAlternativeEmployeesGroup,
): AlternativeEmployeesGroup => {
  return {
    id: employeesGroup.id,
    name: employeesGroup.name,
    isAssigned: employeesGroup.isAssigned,
    employeesCount: Number(employeesGroup.employeesCount),
  };
};

const convertEmployeesGroups = (
  employeesGroups: IAlternativeEmployeesGroup[],
): AlternativeEmployeesGroup[] => {
  return employeesGroups.map(convertEmployeesGroup);
};

const convertAlternativeEmployee = (
  employee: IAlternativeEmployee,
): AlternativeEmployee => {
  return {
    ...employee,
    isLocked:
      employee.isAssigned &&
      employee.deadlineAt &&
      moment().isBefore(employee.deadlineAt)
        ? false
        : true,
  };
};

const convertAlternativeEmployees = (
  employees: IAlternativeEmployee[],
): AlternativeEmployee[] => {
  return employees.map(convertAlternativeEmployee);
};

const convertAlternativeContent = (
  content: IAlternativeContent,
): AlternativeContent => {
  return {
    translations: content.data
      .filter((trans) => trans.title !== null)
      .map((translation) => ({
        language: translation.language,
        title: translation.title,
        description: translation.description ?? '',
      })),
  };
};

const convertAlternativeBenefit = (
  benefit: IAlternativeBenefit,
): AlternativeBenefit => {
  return {
    ...benefit,
    value: benefit.value ?? NaN,
    investmentAmount: benefit.investmentAmount ?? NaN,
  };
};

const convertAlternativeBenefits = (
  benefits: IAlternativeBenefit[],
): AlternativeBenefit[] => {
  return benefits.map(convertAlternativeBenefit);
};

export const converters = {
  getAlternatives: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getAlternatives>>) => {
    return convertBaseAlternatives(data.data);
  },
  getAlternative: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getAlternative>>) => {
    return convertAlternative(data);
  },
  getAlternativeEmployees: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getEmployees>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertAlternativeEmployees(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getEmployeesGroups: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getEmployeesGroups>>) => {
    const items = convertEmployeesGroups(data.data);

    return {
      items,
      total: items.length,
    };
  },
  getAlternativeContent: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getAlternativeContent>>) => {
    return convertAlternativeContent(data);
  },
  getAlternativeBenefits: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getAlternativeBenefits>>) => {
    return convertAlternativeBenefits(data.data);
  },
};
